// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity, Building2, AlertCircle, AlertTriangle, CheckCircle2, Database, GitBranch,
  Inbox, MessageSquare, ServerCrash, Loader2, ArrowRight,
} from 'lucide-react'
import * as api from '../../api/client'
import { useAuth } from '../../auth/AuthContext'

interface Props { onScreenEnter: () => void }

interface VersionInfo {
  version: string
  commit: string
  branch: string
  builtAt: string
}

interface HealthInfo {
  status: 'healthy' | 'unhealthy' | string
  checks: Record<string, { status: string; result?: string; error?: string }>
  checked_at: string
}

interface AuditEvent {
  id: number
  actor_role: string
  actor_account: string
  action: string
  entity_type: string
  entity_id: string
  occurred_at: string
}

interface FeedbackSummary {
  counts?: { open?: number; triaged?: number; promoted?: number; closed?: number }
}

/**
 * SaaS-mode home page. Shows platform health, deploy info, pharmacy roster
 * summary, recent webhook activity, recent feedback. NO pharmacy-scoped
 * data (no Rx, no patients, no queues).
 *
 * This is what an Owl SaaS admin sees when they have no active pharmacy.
 * The moment they Enter a pharmacy (or sign in as a pharmacy user), the
 * mode flips and HomeOrDashboard routes them to the pharmacy dashboard
 * (OwnerHome) instead.
 */
export default function SaasHome({ onScreenEnter }: Props) {
  const navigate = useNavigate()
  const { me } = useAuth()
  const [version, setVersion] = useState<VersionInfo | null>(null)
  const [health, setHealth] = useState<HealthInfo | null>(null)
  const [healthErr, setHealthErr] = useState<string | null>(null)
  const [pharmacies, setPharmacies] = useState<api.PharmacyListItem[]>([])
  const [archivedCount, setArchivedCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [recentAudit, setRecentAudit] = useState<AuditEvent[]>([])
  const [feedback, setFeedback] = useState<FeedbackSummary | null>(null)
  const [alerts, setAlerts] = useState<api.SaasNotifications | null>(null)

  useEffect(() => { onScreenEnter() }, [onScreenEnter])

  useEffect(() => {
    let cancelled = false
    async function load() {
      const [vr, hr, pa, paAll, ar, fb, al] = await Promise.allSettled([
        fetch('/version.json').then(r => r.json()),
        fetch('/api/health').then(r => r.json()),
        api.listPharmacies(),
        api.listPharmacies({ includeArchived: true }),
        fetch('/api/audit?limit=8').then(r => r.json()),
        fetch('/api/feedback?limit=1').then(r => r.json()),
        api.listSaasNotifications(),
      ])
      if (cancelled) return
      if (vr.status === 'fulfilled') setVersion(vr.value)
      if (hr.status === 'fulfilled') setHealth(hr.value)
      else setHealthErr('Could not reach /api/health')
      if (pa.status === 'fulfilled') setPharmacies(pa.value.items)
      if (paAll.status === 'fulfilled' && pa.status === 'fulfilled') {
        setArchivedCount(paAll.value.count - pa.value.count)
      }
      if (ar.status === 'fulfilled' && Array.isArray(ar.value?.items)) {
        setRecentAudit(ar.value.items)
      }
      if (fb.status === 'fulfilled') setFeedback(fb.value)
      if (al.status === 'fulfilled') setAlerts(al.value)
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [])

  const stateCounts = pharmacies.reduce((acc, p) => {
    acc[p.onboarding_state] = (acc[p.onboarding_state] || 0) + 1
    return acc
  }, {} as Record<string, number>)
  const totalActive = pharmacies.length
  const recentlyOnboarded = [...pharmacies]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, 3)

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>Platform overview</h1>
          <p style={subtitleStyle}>
            Welcome back, {me?.display_name || 'admin'}. You're in SaaS view —
            no pharmacy selected. Cross-tenant administration tools are in the
            sidebar. To work inside one pharmacy, click "Enter" from the
            Pharmacies list.
          </p>
        </div>
      </div>

      {loading ? (
        <div style={loadingStyle}>
          <Loader2 size={18} className="spin" /> Loading platform status…
        </div>
      ) : (
        <div style={gridStyle}>
          {/* Active alerts (most important — first row, full width if any) */}
          {alerts && alerts.count > 0 && (
            <Card
              icon={<AlertTriangleIcon />}
              title="Active alerts"
              action={
                <button onClick={() => navigate('/notifications')} style={linkBtnStyle}>
                  View all <ArrowRight size={12} />
                </button>
              }
              wide
            >
              <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                {alerts.counts_by_severity.error > 0 && (
                  <SeverityCount label="errors" count={alerts.counts_by_severity.error} color="#991b1b" />
                )}
                {alerts.counts_by_severity.warn > 0 && (
                  <SeverityCount label="warnings" count={alerts.counts_by_severity.warn} color="#a16207" />
                )}
                {alerts.counts_by_severity.info > 0 && (
                  <SeverityCount label="info" count={alerts.counts_by_severity.info} color="#1e40af" />
                )}
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {alerts.items.slice(0, 5).map(a => (
                  <li key={a.id} style={alertRowStyle}>
                    <span style={{
                      display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                      background: a.severity === 'error' ? '#991b1b'
                                : a.severity === 'warn'  ? '#a16207'
                                : '#1e40af',
                      marginRight: 8, flexShrink: 0,
                    }} />
                    <button onClick={() => navigate(a.link)} style={alertLinkStyle}>
                      {a.title}
                    </button>
                    <span style={mutedSmallStyle}>{a.detail}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Platform health */}
          <Card icon={<Activity size={16} />} title="Platform health">
            {healthErr ? (
              <Status icon="bad" label="Functions unreachable" detail={healthErr} />
            ) : health ? (
              <>
                <Status
                  icon={health.status === 'healthy' ? 'good' : 'bad'}
                  label={health.status === 'healthy' ? 'All systems healthy' : `Status: ${health.status}`}
                />
                {Object.entries(health.checks).map(([k, v]) => (
                  <Status
                    key={k}
                    icon={v.status === 'ok' ? 'good' : 'bad'}
                    label={`${k.toUpperCase()}: ${v.status}`}
                    detail={v.error}
                  />
                ))}
                <div style={mutedSmallStyle}>
                  Checked {timeAgo(health.checked_at)}
                </div>
              </>
            ) : null}
          </Card>

          {/* Deploy info */}
          <Card icon={<GitBranch size={16} />} title="Deployment">
            {version ? (
              <>
                <KV label="Version" value={version.version} />
                <KV label="Branch" value={version.branch} mono />
                <KV label="Commit" value={(version.commit || '').slice(0, 7)} mono />
                <KV label="Built" value={timeAgo(version.builtAt)} />
              </>
            ) : <div style={mutedSmallStyle}>—</div>}
          </Card>

          {/* Pharmacy roster summary */}
          <Card
            icon={<Building2 size={16} />}
            title="Pharmacy roster"
            action={
              <button onClick={() => navigate('/admin/pharmacies')} style={linkBtnStyle}>
                Manage <ArrowRight size={12} />
              </button>
            }
          >
            <KV label="Total active" value={String(totalActive)} />
            {Object.entries(stateCounts).sort(([a], [b]) => a.localeCompare(b)).map(([state, n]) => (
              <KV
                key={state}
                label={state.replaceAll('_', ' ')}
                value={String(n)}
                pillColor={stateColor(state)}
              />
            ))}
            {archivedCount > 0 && (
              <KV label="Archived (hidden)" value={String(archivedCount)} muted />
            )}
          </Card>

          {/* Recently onboarded */}
          <Card icon={<Inbox size={16} />} title="Recently onboarded">
            {recentlyOnboarded.length === 0 ? (
              <div style={mutedSmallStyle}>No pharmacies yet.</div>
            ) : (
              recentlyOnboarded.map(p => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/admin/pharmacies/${p.id}`)}
                  style={rosterRowStyle}
                >
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</div>
                    <div style={mutedSmallStyle}>
                      {p.onboarding_state.replaceAll('_', ' ')} ·
                      {' '}
                      {timeAgo(p.created_at)}
                    </div>
                  </div>
                  <ArrowRight size={14} style={{ color: 'var(--color-text-mute)' }} />
                </button>
              ))
            )}
          </Card>

          {/* Recent audit (focus on cross-tenant ops + webhooks) */}
          <Card
            icon={<Database size={16} />}
            title="Recent activity"
            action={
              <button onClick={() => navigate('/admin/audit')} style={linkBtnStyle}>
                Audit log <ArrowRight size={12} />
              </button>
            }
            wide
          >
            {recentAudit.length === 0 ? (
              <div style={mutedSmallStyle}>No recent events.</div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {recentAudit.slice(0, 6).map(a => (
                  <li key={a.id} style={auditRowStyle}>
                    <code style={auditActionStyle}>{a.action}</code>
                    <span style={mutedSmallStyle}>
                      {' '}by {a.actor_account || a.actor_role}
                      {' · '}
                      {timeAgo(a.occurred_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Feedback */}
          <Card
            icon={<MessageSquare size={16} />}
            title="Feedback inbox"
            action={
              <button onClick={() => navigate('/admin/feedback')} style={linkBtnStyle}>
                Open <ArrowRight size={12} />
              </button>
            }
          >
            {feedback?.counts ? (
              <>
                <KV label="Open" value={String(feedback.counts.open ?? 0)}
                  pillColor={feedback.counts.open ? '#a16207' : undefined} />
                <KV label="Triaged" value={String(feedback.counts.triaged ?? 0)} />
                <KV label="Promoted" value={String(feedback.counts.promoted ?? 0)} />
              </>
            ) : <div style={mutedSmallStyle}>—</div>}
          </Card>
        </div>
      )}
    </div>
  )
}

// ======================================================================
// Building blocks
// ======================================================================

function Card({ icon, title, action, children, wide }: {
  icon: React.ReactNode
  title: string
  action?: React.ReactNode
  children: React.ReactNode
  wide?: boolean
}) {
  return (
    <section style={{ ...cardStyle, gridColumn: wide ? '1 / -1' : 'auto' }}>
      <header style={cardHeaderStyle}>
        <span style={cardTitleStyle}>{icon} {title}</span>
        {action}
      </header>
      <div style={cardBodyStyle}>{children}</div>
    </section>
  )
}

function Status({ icon, label, detail }: { icon: 'good' | 'bad' | 'warn'; label: string; detail?: string }) {
  const Icon =
    icon === 'good' ? CheckCircle2 :
    icon === 'bad'  ? ServerCrash :
    AlertCircle
  const color =
    icon === 'good' ? '#166534' :
    icon === 'bad'  ? '#991b1b' :
    '#a16207'
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
      <Icon size={14} style={{ color, flexShrink: 0, marginTop: 2 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        {detail && <div style={mutedSmallStyle}>{detail}</div>}
      </div>
    </div>
  )
}

function KV({ label, value, mono, muted, pillColor }: {
  label: string; value: string; mono?: boolean; muted?: boolean; pillColor?: string
}) {
  return (
    <div style={kvRowStyle}>
      <span style={{ ...kvLabelStyle, color: muted ? 'var(--color-text-mute)' : undefined }}>{label}</span>
      <span style={{
        ...kvValueStyle,
        fontFamily: mono ? 'monospace' : undefined,
        fontSize: mono ? 12 : 13,
        color: pillColor || (muted ? 'var(--color-text-mute)' : 'var(--color-text)'),
        fontWeight: pillColor ? 600 : 500,
      }}>
        {value}
      </span>
    </div>
  )
}

function SeverityCount({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{count}</span>
      <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>{label}</span>
    </div>
  )
}

function AlertTriangleIcon() {
  return <AlertTriangle size={16} style={{ color: '#a16207' }} />
}

// ======================================================================
// Helpers
// ======================================================================

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime()
  if (!t || isNaN(t)) return '—'
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (sec < 60)   return `${sec}s ago`
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`
  return `${Math.floor(sec / 86400)}d ago`
}

function stateColor(state: string): string | undefined {
  switch (state) {
    case 'active':                return '#166534'
    case 'pending_baa':
    case 'pending_admin':
    case 'pending_ingest_setup':  return '#a16207'
    case 'suspended':             return '#991b1b'
  }
  return undefined
}

// ======================================================================
// Styles
// ======================================================================

const pageStyle: React.CSSProperties = {
  maxWidth: 'var(--page-max, 1200px)', margin: '0 auto', padding: '24px 24px 48px',
}
const headerStyle: React.CSSProperties = { marginBottom: 24 }
const titleStyle: React.CSSProperties = { fontSize: 22, fontWeight: 600, margin: 0 }
const subtitleStyle: React.CSSProperties = {
  margin: '6px 0 0', fontSize: 13.5, color: 'var(--color-text-soft)', maxWidth: '100%',
}
const loadingStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: 32,
  color: 'var(--color-text-soft)', fontSize: 14,
}
const gridStyle: React.CSSProperties = {
  display: 'grid', gap: 16,
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  display: 'flex', flexDirection: 'column',
}
const cardHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px',
  borderBottom: '1px solid var(--color-border)',
}
const cardTitleStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  fontSize: 13, fontWeight: 600,
  color: 'var(--color-text)',
}
const cardBodyStyle: React.CSSProperties = { padding: '12px 16px' }

const kvRowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '4px 0',
}
const kvLabelStyle: React.CSSProperties = { fontSize: 12.5, color: 'var(--color-text-soft)', textTransform: 'capitalize' }
const kvValueStyle: React.CSSProperties = { fontSize: 13, color: 'var(--color-text)', fontWeight: 500 }
const mutedSmallStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--color-text-mute)' }

const linkBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 8px', fontSize: 11.5, fontWeight: 500,
  background: 'transparent',
  color: 'var(--color-primary)',
  border: 'none', borderRadius: 4,
  cursor: 'pointer', fontFamily: 'inherit',
}
const rosterRowStyle: React.CSSProperties = {
  width: '100%', display: 'flex', alignItems: 'center',
  padding: '8px 0', background: 'transparent', border: 'none',
  borderBottom: '1px solid var(--color-border)',
  cursor: 'pointer', fontFamily: 'inherit',
  textAlign: 'left' as const,
}
const auditRowStyle: React.CSSProperties = {
  fontSize: 12.5, lineHeight: 1.6,
  padding: '4px 0',
  borderBottom: '1px solid var(--color-border)',
}
const auditActionStyle: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 11.5,
  background: 'var(--color-surface-alt, rgba(0,0,0,0.04))',
  padding: '1px 6px', borderRadius: 3,
  color: 'var(--color-text)',
}

const alertRowStyle: React.CSSProperties = {
  fontSize: 12.5, lineHeight: 1.5,
  padding: '6px 0',
  borderBottom: '1px solid var(--color-border)',
  display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
}
const alertLinkStyle: React.CSSProperties = {
  background: 'transparent', border: 'none',
  fontSize: 13, fontWeight: 500,
  color: 'var(--color-primary)',
  cursor: 'pointer', padding: 0, fontFamily: 'inherit',
  textAlign: 'left' as const,
}
